import { useEffect, useState } from 'react';
import { api, ApiCallError } from '../api/client';
import { formatMoney, formatRelativeTime } from '@xiaopeiban/shared';
import type { ChildDashboard, ChildSummary } from '@xiaopeiban/shared';
import WhisperPanel from '../components/WhisperPanel';

export default function DashboardPage() {
  const [children, setChildren] = useState<ChildSummary[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [dashboard, setDashboard] = useState<ChildDashboard | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState<number | null>(null);

  useEffect(() => {
    api.listChildren()
      .then(r => {
        setChildren(r.children);
        if (r.children.length > 0) setSelectedId(r.children[0].id);
        else setLoading(false);
      })
      .catch(e => { setErr(niceError(e)); setLoading(false); });
  }, []);

  useEffect(() => {
    if (selectedId == null) return;
    setLoading(true);
    api.childDashboard(selectedId)
      .then(d => { setDashboard(d); setErr(null); })
      .catch(e => setErr(niceError(e)))
      .finally(() => setLoading(false));
  }, [selectedId]);

  async function decide(kind: 'chore' | 'task' | 'reward_request', id: number, reject: boolean, points?: number) {
    setDeciding(id);
    try {
      if (kind === 'chore') await api.decideChoreCompletion(id, reject);
      else if (kind === 'task') await api.decideTaskSubmission(id, reject);
      else await api.decideRewardRequest(id, reject, points);
      // refresh
      if (selectedId) setDashboard(await api.childDashboard(selectedId));
    } catch (e) { setErr(niceError(e)); }
    finally { setDeciding(null); }
  }

  const selectedChild = children.find(c => c.id === selectedId);

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title">📊 仪表盘</div>
        <div className="page-subtitle">孩子的学习和成长一览</div>
      </div>

      {children.length === 0 && !loading && (
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🐹</div>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>还没有孩子账号</div>
          <div className="muted">让孩子打开 app 注册，注册时填你的邮箱就能关联到这个后台。</div>
        </div>
      )}

      {children.length > 1 && (
        <div className="card">
          <div style={{ fontWeight: 700, marginBottom: 8 }}>选择孩子</div>
          <div className="flex" style={{ flexWrap: 'wrap' }}>
            {children.map(c => (
              <button
                key={c.id}
                className={`btn ${c.id === selectedId ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setSelectedId(c.id)}
              >
                {c.avatar || '👶'} {c.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {err && <div className="error-box">{err}</div>}

      {selectedChild && dashboard && (
        <>
          <div className="card">
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 12 }}>
              {selectedChild.avatar || '👶'} {selectedChild.name}
            </div>
            <div className="grid grid-2">
              <div className="stat">
                <div className="stat-value">{formatMoney(dashboard.balance)}</div>
                <div className="stat-label">当前马币余额</div>
              </div>
              <div className="stat">
                <div className="stat-value">{dashboard.pets_owned}</div>
                <div className="stat-label">养了多少只宠物</div>
              </div>
              <div className="stat">
                <div className="stat-value">
                  {dashboard.quiz_7d.correct}/{dashboard.quiz_7d.total || '0'}
                </div>
                <div className="stat-label">近 7 天答题正确率</div>
              </div>
              <div className="stat">
                <div className="stat-value">{dashboard.pending_approvals.length}</div>
                <div className="stat-label">等待审批</div>
              </div>
            </div>
          </div>

          <WhisperPanel childId={selectedChild.id} childName={selectedChild.name} />

          {dashboard.pending_approvals.length > 0 && (
            <div className="card">
              <div style={{ fontWeight: 700, marginBottom: 12 }}>🕐 等待审批</div>
              {dashboard.pending_approvals.map(a => (
                <div
                  key={`${a.kind}-${a.id}`}
                  style={{ padding: 12, borderBottom: '1px solid var(--border)', display: 'flex', gap: 8, flexWrap: 'wrap' }}
                >
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontWeight: 700 }}>
                      <span className="tag" style={{ marginRight: 8 }}>{a.kind === 'chore' ? '家务' : a.kind === 'task' ? '任务' : '红包申请'}</span>
                      {a.title}
                      <span className="muted" style={{ marginLeft: 8 }}>+{formatMoney(a.points)}</span>
                    </div>
                    {a.note && <div className="muted" style={{ marginTop: 4 }}>💬 {a.note}</div>}
                    <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                      {formatRelativeTime(a.at)}
                    </div>
                  </div>
                  <div className="flex">
                    <button
                      className="btn btn-secondary"
                      disabled={deciding === a.id}
                      onClick={() => decide(a.kind, a.id, true, a.points)}
                    >
                      拒绝
                    </button>
                    <button
                      className="btn btn-primary"
                      disabled={deciding === a.id}
                      onClick={() => decide(a.kind, a.id, false, a.points)}
                    >
                      批准 +{formatMoney(a.points)}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="card">
            <div style={{ fontWeight: 700, marginBottom: 12 }}>📱 设备适配信息</div>
            {(!dashboard.devices || dashboard.devices.length === 0) && (
              <div className="muted">还没有收到孩子设备信息。孩子打开一次 App 后会自动上报。</div>
            )}
            {(dashboard.devices || []).map(device => (
              <div
                key={device.id}
                style={{ padding: '10px 0', borderBottom: '1px solid var(--border)', fontSize: 14 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: 800 }}>
                    {device.device_type || '设备'} · {device.display_mode === 'pwa' ? 'PWA' : '浏览器'}
                  </div>
                  <div className="muted" style={{ fontSize: 12 }}>{formatRelativeTime(device.updated_at)}</div>
                </div>
                <div className="muted" style={{ marginTop: 4, lineHeight: 1.6 }}>
                  视口 {device.viewport_width || '-'}×{device.viewport_height || '-'} ·
                  屏幕 {device.screen_width || '-'}×{device.screen_height || '-'} ·
                  DPR {device.dpr || '-'} · {device.orientation || '-'} ·
                  触控 {device.touch ? `是 (${device.max_touch_points || 0})` : '否'}
                </div>
                <div className="muted" style={{ marginTop: 2, fontSize: 12 }}>
                  {device.os || 'Unknown OS'} · {device.browser || 'Unknown Browser'} · {device.language || '-'} · {device.timezone || '-'}
                </div>
              </div>
            ))}
          </div>

          <div className="card">
            <div style={{ fontWeight: 700, marginBottom: 12 }}>💸 最近交易</div>
            {dashboard.transactions.length === 0 && <div className="muted">还没有交易记录</div>}
            {dashboard.transactions.map(t => (
              <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 14 }}>
                <div style={{ flex: 1 }}>
                  <div><span className="tag">{t.source_type}</span> {t.reason}</div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{formatRelativeTime(t.created_at)}</div>
                </div>
                <div style={{ fontWeight: 800, color: t.amount > 0 ? 'var(--success)' : 'var(--danger)' }}>
                  {t.amount > 0 ? '+' : ''}{formatMoney(t.amount)}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {loading && !dashboard && <div style={{ textAlign: 'center', padding: 40 }}><span className="spinner" /></div>}
    </div>
  );
}

function niceError(e: unknown): string {
  if (e instanceof ApiCallError) return e.message;
  return (e as Error)?.message || '出错了';
}
